/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import { useNavigate } from "react-router";
import { Box, useTheme, useMediaQuery, Button, Typography, Modal, FormHelperText, IconButton } from "@mui/material";
import "@styles/stylesRegistro.css";
import { PhoneCodeInput } from "@components/PhoneCodeInput";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { BuroValidatorRequestPF, useBuroValidatorMutation } from "@api/sat_apis_new/satApsi";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { AddressData, ApiError, PersonaStructure } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { useLazyGetColonyByIdQuery } from "@api/catalogs";
import { Close as CloseIcon } from "@mui/icons-material";
import { truncateText } from "@helpers/funciones";

interface ModalBuroNipProps {
  expectedCode: string;
  avalAdicional: PersonaStructure;
  address: AddressData;
  open: boolean;
  onClose: () => void;
}

const ModalBuroNip = (props: ModalBuroNipProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { avalAdicional, expectedCode, address, onClose } = props;

  const applicationData = useSelector((state: RootState) => state.register.application);

  const [confirmCode, setConfirmCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const [buroValidator] = useBuroValidatorMutation();
  const [getColony] = useLazyGetColonyByIdQuery();

  const handleClickConfirm = async () => {
    try {
      setIsLoading(true);

      let colonyName = address.other_neighborhood || "";

      if (address.colonia_id) {
        const responseColony = await getColony({ colonyId: address.colonia_id }).unwrap();
        colonyName = responseColony.name || "";
      }

      // se limita a 26 caracteres por campo, ya que es el máximo permitido por BURO
      const first_name = truncateText(avalAdicional.name, 26);
      const first_lastname = truncateText(avalAdicional.last_name, 26);
      const second_lastname = truncateText(avalAdicional.last_name_2, 26);
      const colony_name_trim = truncateText(colonyName, 26);
      const exteriorNumber = truncateText(address.no_ext || "", 20);
      const interiorNumber = truncateText(address.no_int || "", 20);

      const payload_buro: BuroValidatorRequestPF = {
        person_Type: "pf",
        data: {
          rfc: avalAdicional.rfc,
          curp: avalAdicional.curp,
          transaction_number: String(applicationData.id),
          application_code: applicationData.company_application_code,
          legal_name: first_name,
          first_name: first_name,
          second_name: "",
          father_last_name: first_lastname,
          mother_last_name: second_lastname === "" ? "" : second_lastname,
          reenter_NIP: true,
          authorization_label_response: true,
          loan_application_id: String(applicationData.id),
          person_id: String(avalAdicional.id),
          is_endorsement: true,
          address: {
            street: address.street,
            exteriorNumber: exteriorNumber,
            interiorNumber: interiorNumber,
            state: address.state?.name || "",
            municipality: address.locality?.name || "",
            locality: colony_name_trim,
            postalCode: address.cp,
          },
        },
      };

      await buroValidator(payload_buro).unwrap();
      onClose();
      navigate("/producto/creditosimple/docs_aval");
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 409) {
        onClose();
        navigate("/producto/creditosimple/docs_aval");
      } else {
        Sentry.captureException(error, {
          extra: {
            component: "ConsultaBuro",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={props.open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          width: "70%",
          maxWidth: "400px",
          outline: "none",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography fontWeight={"600"} gutterBottom color={theme?.palette?.primary?.dark}>
          Consulta a Buró de crédito
        </Typography>

        <Typography
          variant={isMobileOrTablet ? "body2" : "body1"}
          sx={{ mt: 2, mb: 2 }}
          color={theme?.palette?.primary?.dark}
        >
          <CustomTextCiecBuro type="nipBuroConfirm" />
        </Typography>

        <Typography
          variant={isMobileOrTablet ? "body2" : "body1"}
          sx={{ mt: 2, mb: 2 }}
          color={theme?.palette?.primary?.dark}
        >
          Confirma tu Código
        </Typography>

        <PhoneCodeInput
          name="code_confirm"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setConfirmCode(event.target.value);
          }}
          disabled={isLoading}
        />

        {confirmCode.length === 4 && confirmCode !== expectedCode && (
          <FormHelperText sx={{ mt: 1 }}>
            El código ingresado no es correcto. Por favor verifica el código y vuelve a intentarlo.
          </FormHelperText>
        )}

        <Button
          variant="default"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleClickConfirm}
          disabled={confirmCode !== expectedCode || isLoading}
        >
          Confirmar
        </Button>
      </Box>
    </Modal>
  );
};

export default ModalBuroNip;
